import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { triggerHaptic } from '../../lib/haptics';
import { PRESS } from './insightsUi';

/**
 * Full-screen overlays match the app's other full-screen modals: `fixed inset-0` above the sticky
 * header and tab bar (both z-40), padded for the safe areas, with their own scroll that starts at the top.
 */
const SAFE_AREA_STYLE: React.CSSProperties = {
  paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)',
  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 2rem)',
};

const OVERLAY_BASE = 'fixed inset-0 overflow-y-auto overflow-x-hidden overscroll-contain bg-slate-100 dark:bg-black';

/** A plain full-screen overlay layer with its own scroll, laid over a still-mounted page. */
export function OverlayLayer({ className = '', children }: { className?: string; children: React.ReactNode }) {
  return (
    <div style={SAFE_AREA_STYLE} className={`${OVERLAY_BASE} ${className}`}>
      {children}
    </div>
  );
}

export interface InsightsSubPageProps {
  title: string;
  onBack: () => void;
  children: React.ReactNode;
}

const EXIT_FALLBACK_MS = 400;

/**
 * A full-screen pushed page laid over the main Insights page (which stays mounted and untouched
 * underneath, so its scroll position and state survive). Slides in from the right and back out to the right (300ms, same curve as the
 * trigger expand); under reduced motion it cross-fades. Content stays tappable mid-animation.
 * At rest it has no transform, so `position: fixed` content inside still pins to the viewport.
 */
export function InsightsSubPage({ title, onBack, children }: InsightsSubPageProps) {
  const [entered, setEntered] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const done = useRef(false);
  const fallback = useRef<number | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => {
      cancelAnimationFrame(raf);
      if (fallback.current !== null) window.clearTimeout(fallback.current);
    };
  }, []);

  const finish = () => {
    if (done.current) return;
    done.current = true;
    onBack();
  };

  const handleBack = () => {
    if (leaving) return;
    triggerHaptic('selection');
    setLeaving(true);
    // transitionend may not fire (no transition under some settings), so always finish on a timer too.
    fallback.current = window.setTimeout(finish, EXIT_FALLBACK_MS);
  };

  const visible = entered && !leaving;

  return (
    <div
      role="region"
      aria-label={title}
      style={SAFE_AREA_STYLE}
      onTransitionEnd={(e) => {
        if (leaving && e.target === e.currentTarget) finish();
      }}
      className={`${OVERLAY_BASE} z-50 shadow-[-0.5rem_0_1.5rem_rgba(0,0,0,0.12)] transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] ${
        visible
          ? 'transform-none opacity-100'
          : 'translate-x-full opacity-100 motion-reduce:translate-x-0 motion-reduce:opacity-0'
      }`}
    >
      <div className="max-w-md mx-auto flex flex-col gap-4 px-4 text-left">
        <div>
          <button
            type="button"
            aria-label="Back"
            onClick={handleBack}
            className={`-ml-1.5 inline-flex items-center gap-0.5 rounded-full py-1.5 pr-3 pl-1 text-base text-blue-600 dark:text-blue-400 ${PRESS}`}
          >
            <ChevronLeft className="w-5 h-5" aria-hidden="true" />
            <span>Insights</span>
          </button>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="mt-1 px-1 text-[1.875rem] font-bold tracking-[-0.02em] leading-tight text-slate-950 dark:text-white outline-none"
          >
            {title}
          </h1>
        </div>
        {children}
      </div>
    </div>
  );
}
